import React, { useEffect, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import useWindowSize from '../../../hooks/useWindowSize';
import { cloneDeep } from 'lodash';
import { process as kendoProcess,orderBy, filterBy } from '@progress/kendo-data-query';

import { getter } from '@progress/kendo-react-common';
import { Grid, GridNoRecords, GridColumn as Column, getSelectedState } from '@progress/kendo-react-grid';

import BobLoadingMask from '../../../components/BobLoadingMask';
import BobTableContextMenu from '../../../components/BobTableContextMenu';
import BobActionMenu from '../../../components/BobActionMenu';
import BobWorkbookModeButton from '../../../components/BobWorkbookModeButton';
import {calculateMinColumnWidths} from '../../../helpers/calculateMinColumnWidths';
import {parsePhoneNumber} from 'libphonenumber-js';

import * as BobActions from '../../../actions/bobActions';
import * as DashboardActions from '../../../actions/dashboardActions';
import { kendoGridInlineStyling } from '../../../helpers/kendoGridInlineStyleHelper';
import classes from './Table.module.css';

const idGetter = getter('id');

const initialDataState = {
    sort:   [],
    take:   20,
    skip:   0,
    filter: null,
    group:  null
};

const UserList = ({
    activeNavCompany,
    bobLoading,
    currentUser,
    fetchUserList,
    history,
    resetPassword,
    setSelectedUser,
    users,
    usersMeta,
    setWorkBookMode,
    setUserAction,
    userAction,
    workbookModeEnabled,
    ...props
}) => {
    const [pageable] = useState({ pageSizes: true });
    const [selectedState, setSelectedState] = useState({});
    const [dataState, setDataState] = useState(initialDataState);
    const [emailColumnWidth, setEmailColumnWidth] = useState(300);
    const [take, setTake] = useState(20);

    const gridDivRef = useRef(null);
    const gridRef = useRef(null);

    const windowSize = useWindowSize();

    const onSelectionChange = (event) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: selectedState,
            dataItemKey:   'id',
        });

        setSelectedState(newSelectedState);
    };

    const onHeaderSelectionChange = event => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = cloneDeep(selectedState);
        let selectedStateItems = {};

        const filteredData = orderBy(filterBy(users, dataState.filter), dataState.sort);
        filteredData.forEach((item) => {
            newSelectedState[idGetter(item)] = checked;
            if (checked) {
                selectedStateItems[idGetter(item)] = item;
            }

        });
        if (!checked) {
            selectedStateItems = {};
        }
        setSelectedState(newSelectedState);
    };

    useEffect(() => {
        const columns = gridRef.current.props.children;
        setEmailColumnWidth(calculateMinColumnWidths(columns, gridDivRef.current.offsetWidth, 'email', 300, true));
    }, [windowSize]);
    
    useEffect(() => {
        const init = async () => {
            const options = {
                limit: 200,
                page:  1
            };
            await fetchUserList(activeNavCompany.id, options);
        };

        if (activeNavCompany.id) init();
    }, [activeNavCompany.id]);

    useEffect(() => {
        if (!workbookModeEnabled) {
            initialDataState.take = take;
            setDataState(initialDataState);
        }
    }, [workbookModeEnabled]);

    const onContextMenuClick = ( dataItem, actionType ) => {
        setSelectedUser(dataItem);
        setUserAction(actionType);
    };

    const actionsCell = props => {
        return (
            <td className={props.className} style={props.style}>
                <BobTableContextMenu
                    type="Users"
                    userData={props.dataItem}
                    handleResetPassword={(userData) => handleResetPassword(userData)}
                    handleEditUser={() => onContextMenuClick(props.dataItem, 'update')}
                />
            </td>
        );
    };

    const handleUserNameClick = (user) => {
        setSelectedUser(user);
    };

    const onDataStateChange = e => {
        setTake(e.dataState.take);
        setDataState(e.dataState);
    };

    const nameCell = props => {
        if (currentUser.isSuperUser || currentUser.id === props.dataItem.id) {
            return (
                <td className={props.className} style={props.style}>
                    <div>
                        <span
                            className={classes.NameField}
                            aria-hidden="true"
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                                handleUserNameClick(
                                    props.dataItem
                                )
                            }
                        >
                            {props.dataItem.firstName}
                        </span>
                    </div>
                </td>
            );
        } else {
            return (
                <td className={props.className} style={props.style}>
                    {props.dataItem.firstName}
                </td>
            );
        }
    };

    const renderPhoneNumberCell = props => {
        let formattedNumber = '';

        if (props.dataItem.phone && !isNaN(props.dataItem.phone)) {
            formattedNumber = parsePhoneNumber(props.dataItem.phone);
            if (formattedNumber) formattedNumber = formattedNumber.formatInternational();
        }

        return (
            <td className={props.className} style={props.style}>
                {formattedNumber}
            </td>
        );
    };

    const handleResetPassword = async (userData) => {
        await resetPassword(userData.id);
    };

    const handleWorkbookMode = () => {
        setWorkBookMode(!workbookModeEnabled);
    };

    const actionFilterCell = props => {
        return (
            <div />
        );
    };

    const renderSuperUserCell = props => {
        const { dataItem } = props;
        return (
            <td className={props.className} style={props.style}>
                {dataItem.isSuperUser ? 'Yes' : null }
            </td>
        );
    };

    return (
        <React.Fragment>
            <div ref={gridDivRef} className={classes.Table}>
                <style>
                    { kendoGridInlineStyling }
                </style>
                <div className={classes.TableContainerHead}>
                    <div className={classes.WorkbookModeContainer}>
                        <BobWorkbookModeButton
                            handleWorkbookMode={handleWorkbookMode}
                            workbookMode={workbookModeEnabled}
                        />
                    </div>
                    <div className={classes.ActionButtonContainer}>
                        <BobActionMenu
                            type="Users"
                            handleCreateNewUser={() => setUserAction('create')}
                        />
                    </div>
                </div>
                <Grid
                    ref={gridRef}
                    sortable={{
                        mode: 'multiple'
                    }}
                    className={classes.KendoGrid}
                    data={kendoProcess(users.map( item => ({
                        ...item,
                        'selected': selectedState[idGetter(item)],
                    })), dataState)}
                    {...dataState}
                    pageable={pageable}
                    reorderable
                    filterable={workbookModeEnabled}
                    onSelectionChange={onSelectionChange}
                    onHeaderSelectionChange={onHeaderSelectionChange}
                    dataItemKey={'id'}
                    selectedField={'selected'}
                    selectable={{
                        enabled: true,
                        drag:    false,
                        cell:    false,
                        mode:    'multiple'
                    }}
                    onDataStateChange={onDataStateChange}
                >   
                    {
                        bobLoading.includes('FetchUserList')  ?
                            <GridNoRecords>
                                Loading Users
                            </GridNoRecords>
                            :
                            null
                    }
                    <Column
                        field="selected"
                        width={70}
                        headerSelectionValue={
                            orderBy(filterBy(users, dataState.filter), dataState.sort).length > 0 && 
                            orderBy(filterBy(users, dataState.filter), dataState.sort).findIndex((item) => !selectedState[idGetter(item)]) === -1
                        }
                        filterable={false}
                        sortable={false}
                    />
                    <Column
                        title="First Name"
                        width={300}
                        cell={nameCell}
                        field="firstName"
                    />
                    <Column field="lastName" title="Last Name" width={300} />
                    <Column field="email" title="Email Address" width={emailColumnWidth} />
                    { currentUser.isSuperUser ? 
                        <Column 
                            field="isSuperUser" 
                            title="Super User" 
                            width={100}
                            cell={renderSuperUserCell}
                        />
                        : null
                    }
                    <Column field="userTypesList" title="User Types" width={300} />
                    <Column
                        field="phone"
                        title="Phone Number"
                        width={currentUser.isSuperAdmin ? 200 : 300}
                        cell={renderPhoneNumberCell}
                    />
                    <Column
                        field=""
                        title=""
                        width={75}
                        cell={actionsCell}
                        locked={true}
                        sortable={false}
                        filterable={false}
                        filterCell={actionFilterCell}
                    />
                </Grid>
                {
                    bobLoading.includes('FetchUserList') || bobLoading.includes('ResetPassword')?
                        <BobLoadingMask
                            className={classes.Loader}
                        />
                        :
                        null
                }
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = state => ({
    activeNavCompany:    state.bob.activeNavCompany,
    bobLoading:          state.bob.bobLoading,
    currentUser:         state.currentUser,
    users:               state.dashboard.users,
    usersMeta:           state.dashboard.usersMeta,
    workbookModeEnabled: state.dashboard.workbookModeEnabled
});

const mapDispatchToProps = ({
    ...BobActions,
    ...DashboardActions
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UserList));
